import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../config/supabase';

const FileContext = createContext();

export const useFileContext = () => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error('useFileContext must be used within a FileProvider');
  }
  return context;
};

export const FileProvider = ({ children, emiratesId }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentEmiratesId, setCurrentEmiratesId] = useState(emiratesId);

  const loadFilesFromSupabase = useCallback(async (userEmiratesId = currentEmiratesId) => {
    if (!userEmiratesId) {
      console.log('No Emirates ID provided, skipping file load');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // List files in the user-specific folder: {emiratesId}/
      const userFolder = `${userEmiratesId}/`;
      console.log(`Loading files for user: ${userEmiratesId} from folder: ${userFolder}`);
      
      const { data, error } = await supabase.storage
        .from('AI-doctor')
        .list(userFolder, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        console.error('Error loading files from Supabase:', error);
        return;
      }

      if (data && data.length > 0) {
        // Convert Supabase file list to our file format
        const fileList = data
          .filter(file => file.name !== '.emptyFolderPlaceholder') // Filter out placeholder files
          .map(file => {
            // File path includes the user folder
            const fullPath = `${userFolder}${file.name}`;
            
            // Get public URL for each file
            const { data: publicData } = supabase.storage
              .from('AI-doctor')
              .getPublicUrl(fullPath);

            // Determine file type from name
            const ext = file.name.split('.').pop().toLowerCase();
            let fileType = 'application/octet-stream';
            
            if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'].includes(ext)) {
              fileType = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
            } else if (ext === 'pdf') {
              fileType = 'application/pdf';
            }

            return {
              id: file.id || file.name,
              fileName: file.name,
              filePath: publicData.publicUrl,
              supabasePath: fullPath, // Store full path for deletion
              fileType: fileType,
              fileSize: file.metadata?.size || 0,
              uploadedAt: file.created_at || file.updated_at || new Date().toISOString(),
            };
          });

        setUploadedFiles(fileList);
        console.log(`Loaded ${fileList.length} files from Supabase for user ${userEmiratesId}`);
      } else {
        setUploadedFiles([]);
        console.log(`No files found for user ${userEmiratesId}`);
      }
    } catch (err) {
      console.error('Error loading files:', err);
    } finally {
      setLoading(false);
    }
  }, [currentEmiratesId]);

  // Update currentEmiratesId when prop changes
  useEffect(() => {
    if (emiratesId !== currentEmiratesId) {
      setCurrentEmiratesId(emiratesId);
      // Reload files for new user
      if (emiratesId) {
        loadFilesFromSupabase(emiratesId);
      } else {
        setUploadedFiles([]);
      }
    }
  }, [emiratesId, currentEmiratesId, loadFilesFromSupabase]);

  // Load existing files from Supabase on mount
  useEffect(() => {
    if (currentEmiratesId) {
      loadFilesFromSupabase(currentEmiratesId);
    }
  }, [currentEmiratesId, loadFilesFromSupabase]);

  const addUploadedFile = useCallback((fileInfo) => {
    setUploadedFiles(prev => [...prev, {
      ...fileInfo,
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      uploadedAt: new Date().toISOString(),
    }]);
  }, []);

  const removeUploadedFile = useCallback((fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  }, []);

  const clearUploadedFiles = useCallback(() => {
    setUploadedFiles([]);
  }, []);

  const getLatestFile = useCallback(() => {
    return uploadedFiles.length > 0 ? uploadedFiles[uploadedFiles.length - 1] : null;
  }, [uploadedFiles]);

  const getFileByType = useCallback((fileType) => {
    return uploadedFiles.filter(file => file.fileType.includes(fileType));
  }, [uploadedFiles]);

  const value = {
    uploadedFiles,
    loading,
    emiratesId: currentEmiratesId,
    addUploadedFile,
    removeUploadedFile,
    clearUploadedFiles,
    getLatestFile,
    getFileByType,
    loadFilesFromSupabase,
  };

  return (
    <FileContext.Provider value={value}>
      {children}
    </FileContext.Provider>
  );
};
